import React from "react"
import { match } from "ts-pattern"
import { Tooltip } from "@blueprintjs/core"

import Tick from "~/common/react-icons/tick"
import Cross from "~/common/react-icons/cross"
import {
  getScore,
  getScoreFormatted,
  ScoreLevelObject,
} from "~/SmartMatching/helpers"
import styles from "./MatchIndicator.module.css"
import SmartMatch from "~/common/react-icons/smartMatch"

type Props = {
  score: number | undefined
  title?: string
}

const CircularIndicator = ({
  backgroundColor,
  score,
  scoreLevel,
  children,
}: {
  backgroundColor: string
  score: number
  scoreLevel: ScoreLevelObject
  children: React.ReactNode
}) => {
  const content = (
    <div className={styles.circularIndicator} style={{ backgroundColor }}>
      {children}
    </div>
  )

  if (scoreLevel === undefined) {
    return (
      <Tooltip content="No match" placement="top">
        {content}
      </Tooltip>
    )
  }

  const scoreLabel = scoreLevel.label
  const scoreFormatted = getScoreFormatted(score)
  const tooltipContent = scoreLabel
    ? `${scoreLabel} match: ${scoreFormatted}`
    : ""

  return (
    <Tooltip content={tooltipContent} placement="top">
      {content}
    </Tooltip>
  )
}

const MatchIndicator = ({ score }: Props) => {
  const scoreLevel = getScore(score)

  if (score === undefined) {
    return (
      <CircularIndicator
        backgroundColor="var(--alert)"
        scoreLevel={scoreLevel}
        score={score}
      >
        <Cross size={10} color="white" />
      </CircularIndicator>
    )
  }

  return match(scoreLevel.value)
    .with("exact", () => (
      <CircularIndicator
        backgroundColor={scoreLevel.color}
        score={score}
        scoreLevel={scoreLevel}
      >
        <Tick color="white" />
      </CircularIndicator>
    ))
    .with("great", () => (
      <CircularIndicator
        backgroundColor={scoreLevel.color}
        score={score}
        scoreLevel={scoreLevel}
      >
        <SmartMatch color="white" />
      </CircularIndicator>
    ))
    .with("good", () => (
      <CircularIndicator
        backgroundColor={scoreLevel.color}
        score={score}
        scoreLevel={scoreLevel}
      >
        <SmartMatch color="white" />
      </CircularIndicator>
    ))
    .with("fair", () => (
      <CircularIndicator
        backgroundColor={scoreLevel.color}
        score={score}
        scoreLevel={scoreLevel}
      >
        <SmartMatch color="white" />
      </CircularIndicator>
    ))
    .with("limited", () => (
      <CircularIndicator
        backgroundColor={scoreLevel.color}
        score={score}
        scoreLevel={scoreLevel}
      >
        <SmartMatch color="white" />
      </CircularIndicator>
    ))
    .exhaustive()
}

export default MatchIndicator
